import React from 'react'
import {
  Anchor,
  Flex,
  PinInput,
  Spacer,
  Text,
  Transition,
} from '@sendoutcards/quantum-design-ui'
import { useState } from 'src/hooks'
import {
  PhoneNumberInput,
  VerifyPhoneNumberFailureFragment,
} from 'src/graphql/generated/graphql'
import { getFormattedNumber } from 'src/onboarding/components/PhoneNumberVerification'

const FONT_FAMILY = 'Montserrat, sans-serif'

const CodeVerification = (props: {
  resourceType: 'FREE_CARD' | 'GIFT_CARD' | 'OTHER'
  phoneNumber: PhoneNumberInput
  verifyPhoneNumber: (
    phoneNumber: PhoneNumberInput,
    verificationCode: string,
  ) => Promise<void | VerifyPhoneNumberFailureFragment>
  onFailure: (failure: VerifyPhoneNumberFailureFragment) => void
  onNeedNewCode: (phoneNumber: PhoneNumberInput) => void
}) => {
  const {
    resourceType,
    phoneNumber,
    verifyPhoneNumber,
    onFailure,
    onNeedNewCode,
  } = props

  const [verificationCode, setVerificationCode] = useState('')
  const [failureMessage, setFailureMessage] = useState<string>()
  const [isLoading, setIsLoading] = useState(false)

  const formattedNumber = getFormattedNumber(
    phoneNumber.countryIsoCode,
    phoneNumber.nationalNumber,
  )

  const verify = async (verificationCode: string) => {
    if (verificationCode.length !== 6) {
      return setFailureMessage('Verification code must 6 digits.')
    }

    setIsLoading(true)
    try {
      const result = await verifyPhoneNumber(phoneNumber, verificationCode)
      if (result?.__typename === 'VerifyPhoneNumberFailure') {
        onFailure(result)
      }
    } catch (error) {
      setFailureMessage(error?.toString() ?? 'Failed to verify phone number.')
    } finally {
      setIsLoading(false)
    }
  }

  const resourceName = resourceType === 'FREE_CARD' ? 'free card' : 'gift card'

  const caption =
    resourceType !== 'OTHER' ? `Your ${resourceName} is one click away.` : ''

  if (isLoading) {
    return <Transition isLoading={isLoading} title="Verifying Code..." />
  }
  return (
    <form
      onSubmit={event => {
        event.preventDefault()
        verify(verificationCode)
      }}
      autoComplete="on"
    >
      <Spacer space="x2" />
      <Text type="subtitle" weight="bold" alignment="center">
        {'Enter your code'}
      </Text>
      <Spacer space="x2" />
      <Text type="caption" alignment="center">
        Verify it's you. Enter the code sent to
      </Text>
      <Text type="subtitle" weight="semiBold" alignment="center">
        {` ${formattedNumber} `}
      </Text>
      <Text type="caption" alignment="center">
        {caption}
      </Text>
      <Spacer space="x4" />
      <div style={{ display: 'flex', justifyContent: 'center' }}>
        <PinInput
          length={6}
          hasFocus={true}
          onChange={setVerificationCode}
          onComplete={verify}
          message={
            failureMessage
              ? {
                  type: 'danger',
                  content: failureMessage,
                }
              : undefined
          }
        />
      </div>
      <Spacer space="x4" />
      <Flex flexDirection="column" alignItems="center">
        <button
          style={{
            borderRadius: '16px',
            display: 'flex',
            justifyContent: 'center',
            boxShadow: '0px 4px 32px 0px rgba(0, 0, 0, 0.08)',
            padding: '8px 20px',
            fontFamily: FONT_FAMILY,
            alignItems: 'center',
            fontWeight: 600,
            color: '#fff',
            fontSize: '16px',
            backgroundColor: '#404040',
            cursor: 'pointer',
            width: '100%',
            maxWidth: '230px',
            height: '48px',
          }}
        >
          Verify Code
        </button>
        <Spacer space="x2" />
        <Anchor
          title="I didn't receive a code"
          isDecorated={true}
          onClick={() => onNeedNewCode(phoneNumber)}
        />
      </Flex>
    </form>
  )
}

export default CodeVerification
